import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LayoutGrid,
  Loader2,
  MoreVertical,
  Plus,
  Search,
  Star,
  Trash2,
  Copy,
} from 'lucide-react';
import {
  createWhiteboard,
  deleteWhiteboard,
  duplicateWhiteboard,
  listWhiteboards,
  updateWhiteboard,
} from './services/whiteboards.service.ts';
import type { Whiteboard, WhiteboardFilter } from './types/whiteboard.types.ts';
import { authService } from '../../services/auth.service.ts';
import PageHeader, { pageHeaderPrimaryButtonClass } from '../dashboard/components/PageHeader.tsx';

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Az önce';
  if (minutes < 60) return `${minutes} dk önce`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} sa önce`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} gün önce`;
  return new Date(timestamp).toLocaleDateString('tr-TR');
}

const WhiteboardsPage: React.FC = () => {
  const navigate = useNavigate();
  const [boards, setBoards] = useState<Whiteboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<WhiteboardFilter>('all');
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const userId = authService.getUser()?.id;

  const load = async () => {
    setLoading(true);
    try {
      setBoards(await listWhiteboards());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(() => {
    let list = [...boards];
    if (filter === 'owned' && userId) {
      list = list.filter((b) => b.ownerId === userId);
    }
    if (filter === 'recent') {
      const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      list = list.filter((b) => b.updatedAt >= weekAgo);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((b) => b.title.toLowerCase().includes(q));
    }
    return list.sort((a, b) => {
      if (a.starred !== b.starred) return a.starred ? -1 : 1;
      return b.updatedAt - a.updatedAt;
    });
  }, [boards, filter, search, userId]);

  const handleCreate = async () => {
    const board = await createWhiteboard();
    navigate(`/products/whiteboards/${board.id}`);
  };

  const handleToggleStar = async (board: Whiteboard, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await updateWhiteboard(board.id, { starred: !board.starred });
    await load();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu beyaz tahtayı silmek istediğinize emin misiniz?')) return;
    await deleteWhiteboard(id);
    setMenuOpenId(null);
    await load();
  };

  const handleDuplicate = async (id: string) => {
    const copy = await duplicateWhiteboard(id);
    setMenuOpenId(null);
    if (copy) navigate(`/products/whiteboards/${copy.id}`);
  };

  const filters: Array<{ id: WhiteboardFilter; label: string }> = [
    { id: 'all', label: 'Tüm beyaz tahtalar' },
    { id: 'owned', label: 'Sahip olduğum' },
    { id: 'recent', label: 'Son 7 gün' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={LayoutGrid}
        title="Beyaz Tahtalar"
        description="Toplantılarınız için işbirlikçi dijital tahtalar oluşturun ve yönetin."
        actions={
          <button type="button" onClick={() => void handleCreate()} className={pageHeaderPrimaryButtonClass}>
            <Plus className="w-5 h-5" />
            Yeni beyaz tahta
          </button>
        }
      />

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Beyaz tahta ara..."
            className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {filters.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                filter === f.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400 gap-3">
          <Loader2 className="w-5 h-5 animate-spin" />
          Yükleniyor...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 bg-white/5 rounded-2xl border border-white/10 border-dashed">
          <LayoutGrid className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h2 className="text-white font-semibold mb-2">Henüz beyaz tahta yok</h2>
          <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
            Zoom&apos;daki gibi boş bir tahta oluşturun, çizin, şekil ekleyin ve toplantıda paylaşın.
          </p>
          <button
            type="button"
            onClick={() => void handleCreate()}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold"
          >
            <Plus className="w-5 h-5" />
            İlk beyaz tahtanızı oluşturun
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((board) => (
            <div
              key={board.id}
              className="group relative bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-purple-500/40 transition"
            >
              <Link to={`/products/whiteboards/${board.id}`} className="block">
                <div className="aspect-video bg-white relative overflow-hidden">
                  {board.thumbnail ? (
                    <img
                      src={board.thumbnail}
                      alt={board.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-slate-100 text-gray-400 text-sm">
                      Boş tahta
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-white font-medium truncate pr-8">{board.title}</h3>
                  <p className="text-gray-500 text-xs mt-1">
                    Düzenlendi: {formatRelativeTime(board.updatedAt)}
                  </p>
                </div>
              </Link>

              <button
                type="button"
                onClick={(e) => void handleToggleStar(board, e)}
                className={`absolute top-3 left-3 p-1.5 rounded-lg transition ${
                  board.starred
                    ? 'text-amber-400 bg-black/40'
                    : 'text-white/70 bg-black/30 opacity-0 group-hover:opacity-100'
                }`}
                title={board.starred ? 'Yıldızı kaldır' : 'Yıldızla'}
              >
                <Star className={`w-4 h-4 ${board.starred ? 'fill-current' : ''}`} />
              </button>

              <div className="absolute top-3 right-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMenuOpenId(menuOpenId === board.id ? null : board.id);
                  }}
                  className="p-1.5 rounded-lg bg-black/30 text-white/80 hover:bg-black/50 opacity-0 group-hover:opacity-100 transition"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
                {menuOpenId === board.id && (
                  <div className="absolute right-0 mt-1 w-40 bg-slate-800 border border-white/10 rounded-lg shadow-xl z-10 py-1">
                    <button
                      type="button"
                      onClick={() => void handleDuplicate(board.id)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-200 hover:bg-white/10"
                    >
                      <Copy className="w-4 h-4" />
                      Kopyala
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(board.id)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-white/10"
                    >
                      <Trash2 className="w-4 h-4" />
                      Sil
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WhiteboardsPage;
